import { BotSequenceActiveSuppressionRow, BotSequenceSuppressedEventRow } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant } from "@swivl-mono/ui";

import { DeliveriesCell } from "../SequenceEvents/SequenceEvents.Shared";
import {
  IdList,
  OUTCOME_LABELS,
  OutcomeChip,
  SEQUENCE_LABELS,
  SuppressionsViewLevel,
  externalIdList,
  formatExpiresIn,
  formatInstant,
  formatSubject,
  formatUser
} from "./SequenceSuppressions.Shared";

interface SuppressionsDetailPanelProps {
  /** Exactly one of event / suppression is set — the panel shows whichever row was clicked. */
  event: BotSequenceSuppressedEventRow | null;
  suppression: BotSequenceActiveSuppressionRow | null;
  viewLevel: SuppressionsViewLevel;
  onViewUserEvents: (userId: number) => void;
}

/**
 * The right-side detail of one clicked row (the CRM interaction model). The agent level
 * shows the human-readable facts; the admin level additionally reveals internal / FMS ids
 * and the engine's recorded actions (timeline, raw status, re-dispatches).
 */
export function SuppressionsDetailPanel({ event, suppression, viewLevel, onViewUserEvents }: SuppressionsDetailPanelProps): React.ReactElement {
  const isAdmin = viewLevel === "admin";
  const title = event ? "Suppressed event" : "Suppression";

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-grey-50">
      {/* No close button here — the shared Panel renders its own top-right X (onToggle). */}
      <div className="sticky top-0 z-20 flex items-center bg-grey-50 px-3 py-3">
        <p className="font-poppins font-semibold">{title}</p>
      </div>

      <div className="flex flex-1 flex-col gap-3 px-3 pb-9">
        {event && <EventDetail event={event} isAdmin={isAdmin} />}
        {suppression && <SuppressionDetail suppression={suppression} isAdmin={isAdmin} onViewUserEvents={onViewUserEvents} />}
      </div>
    </div>
  );
}

/** Shared with the CRM Sequences page's detail panel (ENG-2990) — same card language. */
export function DetailCard(props: { title: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div className="rounded-sm border border-grey-300 bg-white p-4">
      <p className="mb-2 text-xs font-semibold tracking-wide text-grey-500 uppercase">{props.title}</p>
      <div className="flex flex-col gap-2">{props.children}</div>
    </div>
  );
}

/** Shared with the CRM Sequences page's detail panel (ENG-2990). */
export function FactRow(props: { label: string; value: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex gap-4 text-sm">
      <span className="w-32 shrink-0 font-semibold text-grey-600">{props.label}</span>
      <span className="min-w-0 text-grey-900">{props.value}</span>
    </div>
  );
}

function EventDetail({ event, isAdmin }: { event: BotSequenceSuppressedEventRow; isAdmin: boolean }): React.ReactElement {
  const timeline = Object.entries(event.timeline);
  return (
    <>
      <DetailCard title="Decision">
        <FactRow
          label="Outcome"
          value={
            <span className="flex items-center gap-2">
              <OutcomeChip outcome={event.outcome} /> {event.reason}
            </span>
          }
        />
        <FactRow label="Sequence" value={`${SEQUENCE_LABELS[event.sequenceType]}, day ${event.stepOffset}`} />
        {/* localDay is the location's own business day — shown verbatim, never converted. */}
        <FactRow label="Day" value={<span className="font-mono">{event.localDay}</span>} />
        <FactRow label="Time" value={formatInstant(event.occurredAt)} />
        <FactRow label="Attempted" value={<DeliveriesCell deliveries={event.deliveries} />} />
      </DetailCard>

      <DetailCard title="Customer">
        <FactRow label="User" value={formatUser(event.userName, event.userId)} />
        <FactRow label="Location" value={event.locationName ?? "—"} />
        <FactRow label="Subject" value={formatSubject(event.leaseId, event.leadId)} />
      </DetailCard>

      {event.errorDetail && (
        <DetailCard title="Error">
          {/* Full text only here — the table row never truncates an error, it flags one. */}
          <pre className="rounded-md bg-red-50 p-2 font-mono text-xs whitespace-pre-wrap text-red-900 select-all">{event.errorDetail}</pre>
        </DetailCard>
      )}

      {isAdmin && (
        <>
          <DetailCard title="Ids (admin)">
            <IdList
              ids={[
                { label: "Attempt", value: `#${event.attemptId}` },
                { label: "Enrollment", value: `#${event.enrollmentId}` },
                { label: "Org", value: `#${event.orgId}` },
                { label: "Location", value: event.locationId === null ? null : `#${event.locationId}` },
                { label: "User", value: `#${event.userId}` },
                { label: "Tenant", value: event.tenantId === null ? null : `#${event.tenantId}` }
              ]}
            />
            <IdList ids={externalIdList(event.externalIds)} />
          </DetailCard>

          <DetailCard title="Engine actions (admin)">
            {timeline.length === 0 ? (
              <span className="text-sm text-grey-500">No transition stamps recorded.</span>
            ) : (
              <div className="flex flex-col gap-1 text-sm">
                {timeline.map(([stage, at]) => (
                  <span key={stage} className="text-grey-700">
                    {stage} <span className="font-mono">{formatInstant(at)}</span>
                  </span>
                ))}
              </div>
            )}
            {event.redispatchCount !== null && event.redispatchCount > 0 && (
              <span className="text-sm text-grey-600">Re-dispatched {event.redispatchCount}×</span>
            )}
            <IdList
              ids={[
                { label: "Anchor day (location-local)", value: event.anchorDate },
                { label: "Raw status", value: event.rawStatus },
                { label: "Outcome", value: OUTCOME_LABELS[event.outcome] }
              ]}
            />
          </DetailCard>
        </>
      )}
    </>
  );
}

function SuppressionDetail({
  suppression,
  isAdmin,
  onViewUserEvents
}: {
  suppression: BotSequenceActiveSuppressionRow;
  isAdmin: boolean;
  onViewUserEvents: (userId: number) => void;
}): React.ReactElement {
  return (
    <>
      <DetailCard title="Suppression">
        <FactRow
          label="Suppressed until"
          value={
            <span>
              {formatInstant(suppression.suppressUntil)}
              <span className="block text-xs text-grey-500">{formatExpiresIn(suppression.suppressUntil)}</span>
            </span>
          }
        />
        <FactRow label="Sequence" value={SEQUENCE_LABELS[suppression.sequenceType]} />
        <FactRow label="Times extended" value={suppression.commitmentChangeCount} />
        <FactRow label="Last fired" value={suppression.lastFiredAt ? formatInstant(suppression.lastFiredAt) : "never"} />
        <FactRow label="Furthest step" value={suppression.furthestStepFired ?? "—"} />
        <FactRow label="Updated" value={formatInstant(suppression.updatedAt)} />
      </DetailCard>

      <DetailCard title="Customer">
        <FactRow label="User" value={formatUser(suppression.userName, suppression.userId)} />
        <FactRow label="Location" value={suppression.locationName ?? "—"} />
        <FactRow
          label="Subject"
          value={
            <span>
              {formatSubject(suppression.leaseId, suppression.leadId)}
              <span className="block text-xs text-grey-500">{suppression.tenantId === null ? "no tenant" : `tenant #${suppression.tenantId}`}</span>
            </span>
          }
        />
      </DetailCard>

      {isAdmin && (
        <DetailCard title="Ids (admin)">
          <IdList
            ids={[
              { label: "Enrollment", value: `#${suppression.enrollmentId}` },
              { label: "Org", value: `#${suppression.orgId}` },
              { label: "Location", value: suppression.locationId === null ? null : `#${suppression.locationId}` },
              { label: "User", value: `#${suppression.userId}` },
              { label: "Tenant", value: suppression.tenantId === null ? null : `#${suppression.tenantId}` },
              { label: "Anchor day (location-local)", value: suppression.anchorDate },
              { label: "Created", value: formatInstant(suppression.createdAt) }
            ]}
          />
          <IdList ids={externalIdList(suppression.externalIds)} />
        </DetailCard>
      )}

      <div>
        <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={() => onViewUserEvents(suppression.userId)}>
          View this user&apos;s not-sent events →
        </Button>
      </div>
    </>
  );
}
